import { useSearchParams, useNavigate } from 'react-router';
import { useZodForm, type UseZodFormProps, type UseZodFormReturn } from './useZodForm';
import { useCallback, useMemo, useState } from 'react';
import { createQueryObj } from '@repo/util/createQueryObj';
import { definedProps, type DefinedPropsCoercer } from '@repo/util/definedProps';
import type { TypeOfOptional, ZodObjecty } from '@repo/types/Types';
import { getZodDefaults } from '@repo/util/getZodDefaults';
import { useDeepCompareEffect } from 'react-use';
import type { KeepStateOptions, UseFormReset } from 'react-hook-form';

export type OnFilterChange<T extends ZodObjecty | undefined = ZodObjecty | undefined> = (
  newFilter: TypeOfOptional<T>
) => void;

export type UseZodQueryParams<T extends ZodObjecty | undefined = ZodObjecty | undefined> = Omit<
  UseZodFormProps<T>,
  'onSubmit' | 'defaultValues'
> & {
  ignoreList?: string[];
  coerce?: DefinedPropsCoercer;
};

export type UseZodFormOrQueryReturn<Schema extends ZodObjecty> =
  | UseZodFormReturn<Schema>
  | UseZodQueryReturn<Schema>;

export function isZodQueryReturn<Schema extends ZodObjecty>(
  queryOrForm: UseZodFormOrQueryReturn<Schema>
): queryOrForm is UseZodQueryReturn<Schema> {
  return 'queryValues' in queryOrForm;
}

export type UseZodQueryReturn<Schema extends ZodObjecty | undefined = ZodObjecty | undefined> =
  UseZodFormReturn<Schema> & {
    setPartial(data: Partial<TypeOfOptional<Schema>>): void;
    clear(): void;
    queryValues: TypeOfOptional<Schema>;
  };

const paramsToUrl = (
  data: { [key: string]: unknown },
  ignoredKeys?: string[],
  coerce?: DefinedPropsCoercer
) => {
  const newURL = new URL(
    `?${new URLSearchParams(
      definedProps(data, ignoredKeys, coerce).map(
        (nestedArray: unknown[] | string[] | object[]) => {
          return nestedArray.map((item: unknown | string | object) => {
            return String(item);
          });
        }
      )
    ).toString()}`,
    document.location.href
  );

  return newURL;
};

const paramsToUrls = (
  data: { [key: string]: unknown },
  ignoredKeys?: string[],
  coerce?: DefinedPropsCoercer
) => ({
  url: paramsToUrl(data, undefined, coerce),
  asUrl: paramsToUrl(data, ignoredKeys, coerce),
});

export function useZodQuery<Schema extends ZodObjecty | undefined = ZodObjecty | undefined>({
  schema,
  ignoreList: hiddenFields,
  coerce,
  ...options
}: UseZodQueryParams<Schema>): UseZodQueryReturn<Schema> {
  const schemaDefaults = useMemo(
    () => (schema ? (getZodDefaults(schema) as Partial<TypeOfOptional<Schema>>) : undefined),
    [schema]
  );

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = createQueryObj(searchParams);

  const [fromQueryOptions, setFromQueryOptions] = useState<KeepStateOptions>();
  const queryValues = useMemo(() => {
    const res = schema?.safeParse(query);

    if (res && !res.success) {
      console.error('Parsing of query did not succeed!', JSON.stringify(res.error.format(), null, 4));
    }
    return {
      ...schemaDefaults,
      ...(res?.success ? res.data : {}),
    } as TypeOfOptional<Schema>;
  }, [schemaDefaults, query, schema]);

  const setCount = useState(0)[1];
  const form = useZodForm<Schema>({
    ...options,
    schema,
    async onSubmit(data) {
      reset(data);
      setCount((count) => count + 1);
    },
  });

  const reset = useCallback<UseFormReset<TypeOfOptional<Schema>>>(
    (params, keepStateOptions) => {
      params ??= queryValues;
      const { url, asUrl } = paramsToUrls(
        params as { [key: string]: unknown },
        hiddenFields,
        coerce
      );
      form.reset(params, keepStateOptions);

      navigate(asUrl.search, { preventScrollReset: true });
      setFromQueryOptions(keepStateOptions);
    },
    [form, hiddenFields, queryValues, searchParams, navigate, coerce]
  );
  const extraApi = useMemo(
    () => ({
      reset,
      setPartial: (partial?: Partial<TypeOfOptional<Schema>>) => {
        const { url, asUrl } = paramsToUrls(
          {
            ...queryValues,
            ...partial,
          },
          hiddenFields,
          coerce
        );

        navigate(url.search, { preventScrollReset: true });
      },
      clear: () => reset({ ...queryValues, ...schemaDefaults }),
    }),
    [reset, queryValues, hiddenFields, searchParams, schemaDefaults, coerce]
  );
  useDeepCompareEffect(() => {
    form.reset(queryValues, fromQueryOptions);
    setFromQueryOptions({ keepDirty: true, keepDirtyValues: true });
  }, [form.reset, queryValues]);
  return useMemo(
    () => ({
      ...form,
      ...extraApi,
      queryValues,
    }),
    [form, extraApi, queryValues]
  );
}
